import {useNavigate} from "react-router-dom";
import {
  Anchor,
  Button,
  Center,
  CloseButton,
  Container,
  Divider,
  Group,
  LoadingOverlay,
  Paper,
  rem,
  Space,
  Stack,
  Text,
  Title,
  useMantineTheme
} from "@mantine/core";
import {
  firstOrUndefined,
  ImportType,
  useDownloadImportTemplate,
  useGetMeterImportTypesQuery,
  useImportMetersMutation
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useRef, useState} from "react";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {Dropzone, FileWithPath} from "@mantine/dropzone";
import {ImportTypeSelect} from "../../components/select/ImportTypeSelect";
import {IconFile, IconUpload, IconX} from "@tabler/icons-react";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {notifications} from "@mantine/notifications";

export const MeterImportPage = () => {
  const openRef = useRef<() => void>(null);

  const [importType, setImportType] = useState<ImportType | undefined>(undefined)
  const [files, setFiles] = useState<FileWithPath[]>()

  const {baseQueryApi} = useApp()
  const theme = useMantineTheme()
  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const {data, isLoading} = useGetMeterImportTypesQuery();
  const [importMeters, {isLoading: isImporting}] = useImportMetersMutation();
  const {downloadImportTemplate} = useDownloadImportTemplate({baseQueryApi})

  const onDownloadClick = useCallback(() => {
    if (importType) {
      downloadImportTemplate(importType)
    }
  }, [downloadImportTemplate, importType])

  const onSubmit = useCallback(() => {
    if (files && files.length === 1 && importType) {
      importMeters({importType: importType, file: files[0]})
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: t("common.entity.meter.plural", "Meters") + " " + t("common.status.imported", "Imported"),
            message: ""
          });
          navigate(-1)
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
          })
        );
    }
  }, [files, importType, importMeters, t, navigate]);

  const onSelectFileClick = useCallback(() => {
    if (openRef.current) {
      openRef.current()
    }
  }, [])

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.meter} fluid={true} py="md">
          <Center>
            <Paper miw={500} shadow="sm" p="md" py="md" pos="relative">
              <LoadingOverlay visible={isImporting} overlayBlur={2}/>
              <Group position="apart">
                <Title align="center">
                  {t("common.action.import", "Import") + " " + t("common.entity.meter.plural", "Meters")}
                </Title>
                <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
              </Group>
              <Space h="md"/>

              <Stack spacing="sm">
                <ImportTypeSelect
                  options={data ?? []}
                  onImportTypeChange={(types) => setImportType(firstOrUndefined(types))}
                  loading={isLoading}/>

                {importType &&
                  <Anchor component="button" type="button" onClick={onDownloadClick}>
                    {t("common.action.download", "Download")}
                  </Anchor>
                }

                {files && files.length > 0 && <Divider/>}

                <ul>
                  {files?.map((file, index) => {
                    return <li key={index}>{file.name}</li>
                  })}
                </ul>

                <Button onClick={onSubmit} disabled={!importType || !files}>
                  {t("common.action.import", "Import")}
                </Button>

                {files && files.length > 0 && <Divider/>}

                <Dropzone openRef={openRef} onDrop={setFiles} maxFiles={1}>
                  <Group position="center" spacing="xl" style={{minHeight: rem(220), pointerEvents: 'none'}}>
                    <Dropzone.Accept>
                      <IconUpload
                        size="3.2rem"
                        stroke={1.5}
                        color={theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 4 : 6]}
                      />
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <IconX
                        size="3.2rem"
                        stroke={1.5}
                        color={theme.colors.red[theme.colorScheme === 'dark' ? 4 : 6]}
                      />
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <IconFile size="3.2rem" stroke={1.5}/>
                    </Dropzone.Idle>

                    <div>
                      <Text size="xl" inline>
                        Drag file here or click to select file
                      </Text>
                      <Text size="sm" color="dimmed" inline mt={7}>
                        Attach only one file, the file should not exceed 100mb
                      </Text>
                    </div>
                  </Group>
                </Dropzone>

                <Group position="center" mt="md">
                  <Button variant="subtle" onClick={onSelectFileClick}>
                    {t("common.action.selectfile", "Select File")}
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};

